import React, { useState, useEffect } from 'react';
import {
  checkAllServicesEnhanced,
  EnhancedHealthReport,
  EnhancedHealthStatus,
  testLambdaCORS
} from '../utils/enhancedHealthCheck';
import './MicroserviceHealthDashboard.css';

interface MicroserviceHealthDashboardProps {
  refreshInterval?: number; // in milliseconds
  compact?: boolean;
}

const MicroserviceHealthDashboard: React.FC<MicroserviceHealthDashboardProps> = ({
  refreshInterval = 60000, // 1 minute default
  compact = false
}) => {
  const [healthReport, setHealthReport] = useState<EnhancedHealthReport | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastError, setLastError] = useState<string | null>(null);

  useEffect(() => {
    // Initial check
    performHealthCheck();

    // Set up interval for auto-refresh
    const intervalId = setInterval(performHealthCheck, refreshInterval);

    // Cleanup
    return () => clearInterval(intervalId);
  }, [refreshInterval]);

  const performHealthCheck = async () => {
    setIsLoading(true);
    setLastError(null);
    
    try {
      const report = await checkAllServicesEnhanced();
      setHealthReport(report);
    } catch (error) {
      setLastError(error instanceof Error ? error.message : 'Health check failed');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="health-dashboard loading">
        <p>Checking microservices health...</p>
      </div>
    );
  }

  if (lastError) {
    return (
      <div className="health-dashboard error">
        <p>Error checking health: {lastError}</p>
      </div>
    );
  }

  if (!healthReport) {
    return null;
  }

  if (compact) {
    return (
      <div className="health-dashboard compact">
        <div className="health-summary">
          <span className="health-label">Services:</span>
          <span className="health-stats">
            {healthReport.summary.healthy}/{healthReport.summary.total} healthy
          </span>
          {healthReport.summary.unhealthy > 0 && (
            <span className="health-warning">
              Warning: {healthReport.summary.unhealthy} unhealthy
            </span>
          )}
          {healthReport.summary.corsErrors > 0 && (
            <span className="health-cors-warning">
              {healthReport.summary.corsErrors} CORS issues
            </span>
          )}
          {healthReport.summary.unknown > 0 && (
            <span className="health-warning">
              {healthReport.summary.unknown} unknown
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="health-dashboard">
      <h3>Microservice Health Status</h3>
      
      <div className="health-summary">
        <div className="summary-item">
          <span className="summary-label">Total Services:</span>
          <span className="summary-value">{healthReport.summary.total}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Healthy:</span>
          <span className="summary-value healthy">{healthReport.summary.healthy}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Unhealthy:</span>
          <span className="summary-value unhealthy">{healthReport.summary.unhealthy}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">CORS Errors:</span>
          <span className="summary-value cors-error">{healthReport.summary.corsErrors}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Unknown:</span>
          <span className="summary-value unknown">{healthReport.summary.unknown}</span>
        </div>
      </div>

      <div className="health-grid-container">
        <div className="health-grid">
          <div className="grid-header">
            <div className="grid-cell header-cell">Service Name</div>
            <div className="grid-cell header-cell">Status</div>
            <div className="grid-cell header-cell">Response Time</div>
            <div className="grid-cell header-cell">Endpoint</div>
            <div className="grid-cell header-cell">Issues</div>
          </div>
          {healthReport.services.map((service) => (
            <div key={service.service} className={`service-grid-row ${service.status}`}>
              <div className="grid-cell service-name">
                <strong>{service.displayName}</strong>
              </div>
              <div className="grid-cell status-cell">
                <span 
                  className={`status-indicator ${service.status}`}
                >
                  {service.status === 'cors-error' ? 'CORS Issue' : 
                   service.status === 'healthy' ? 'Healthy' : 
                   service.status === 'unhealthy' ? 'Unhealthy' : 'Unknown'}
                </span>
              </div>
              <div className="grid-cell response-time">{service.responseTime}ms</div>
              <div className="grid-cell endpoint">{service.endpoint}</div>
              <div className="grid-cell issues">
                {service.status === 'cors-error' ? (
                  <span className="cors-warning">CORS headers missing - API responding but browser blocked</span>
                ) : service.status === 'unhealthy' ? (
                  <span className="error-text">{service.error || 'API not responding or network error'}</span>
                ) : service.status === 'unknown' ? (
                  <span className="unknown-text">{service.error || 'Status unknown'}</span>
                ) : (
                  <span className="success-text">No issues detected</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="health-footer">
        <button
          className="refresh-button"
          onClick={performHealthCheck}
          disabled={isLoading}
        >
          {isLoading ? 'Checking...' : 'Refresh Now'}
        </button>
        
        <button
          className="refresh-button"
          onClick={() => testLambdaCORS()}
          style={{ marginLeft: '10px', backgroundColor: '#ff9800' }}
        >
          Test CORS
        </button>
        <span className="last-updated">
          Last updated: {healthReport.timestamp.toLocaleString()}
        </span>
      </div>
    </div>
  );
};

export default MicroserviceHealthDashboard;